import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import {Users, Shield, TrendingUp, Heart, Award, Briefcase, CheckCircle2, ArrowRight} from 'lucide-react'
import { Link } from 'react-router-dom'

function Careers() {
  const benefits = [
    {
      icon: <Award className="w-6 h-6" />,
      title: "Competitive Pay",
      description: "Industry-leading wages and performance bonuses"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Career Growth",
      description: "Clear advancement paths and professional development"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Comprehensive Training",
      description: "Paid training and certifications including First Aid & CPR"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Benefits Package",
      description: "Health, dental, and extended benefits for full-time staff"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Supportive Team",
      description: "Work with experienced professionals in a collaborative environment"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Flexible Scheduling",
      description: "Full-time, part-time, and casual positions available"
    }
  ]

  const positions = [
    {
      title: "Security Guard",
      type: "Full-Time / Part-Time",
      location: "Multiple Locations",
      description: "Join our team as a uniformed security guard protecting commercial and residential properties."
    },
    {
      title: "Mobile Patrol Officer",
      type: "Full-Time",
      location: "Multiple Locations",
      description: "Conduct mobile patrols across multiple sites with company vehicle provided."
    },
    {
      title: "Concierge Security",
      type: "Full-Time",
      location: "Major Cities",
      description: "Provide professional front desk security services for luxury residential properties."
    },
    {
      title: "Site Supervisor",
      type: "Full-Time",
      location: "Various Locations",
      description: "Lead and supervise security teams at client sites. Experience required."
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8 text-sm font-medium text-blue-700">
              <Users className="w-4 h-4" />
              <span>Join Our Team</span>
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-8 leading-tight text-slate-900">
              Build Your Career in Security
            </h1>
            <p className="text-xl sm:text-2xl text-slate-600 leading-relaxed mb-10">
              Join a leading North American security company and become part of a professional team dedicated to protecting communities.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-semibold text-lg rounded-xl hover:bg-blue-700 transition-all shadow-lg"
            >
              Apply Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-slate-900">
              Why Join Sentrio?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We invest in our people with competitive benefits, ongoing training, and real career advancement opportunities
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border-2 border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">{benefit.title}</h3>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">
                What We're Looking For
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We seek motivated individuals who are committed to professionalism, integrity, and excellent customer service.
              </p>

              <div className="space-y-4">
                <h3 className="font-bold text-slate-900 mb-4">Requirements:</h3>
                {[
                  "19+ years of age",
                  "Valid Security Worker License for your region (or willingness to obtain)",
                  "Clean criminal record check",
                  "Excellent communication skills",
                  "Professional appearance and attitude",
                  "Reliable and punctual",
                  "First Aid certification (asset)",
                  "Previous security experience (asset)"
                ].map((req, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{req}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://static.lumi.new/b3/b3f313f2a0fe975033f02520e6596b45.jpg" 
                alt="Security professional" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-slate-900">
              Open Positions
            </h2>
            <p className="text-xl text-slate-600">
              Explore current opportunities and find your perfect role
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {positions.map((position, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border-2 border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-2xl font-bold text-slate-900">{position.title}</h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                    {position.type}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 mb-4">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm">{position.location}</span>
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">{position.description}</p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-4 transition-all group"
                >
                  Apply Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-600 mb-6">Don't see the right position? We're always looking for talented security professionals.</p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-xl hover:bg-slate-800 transition-all"
            >
              Submit General Application
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Start Your Career?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join our team of dedicated security professionals and make a difference in your community.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 px-10 py-5 bg-white text-blue-600 font-bold text-lg rounded-xl hover:bg-blue-50 transition-all shadow-xl"
          >
            Apply Today
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Careers